import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Zap, HelpCircle, FlaskConical, GraduationCap, Users, Target, Info, ShieldCheck, TrendingUp, Wrench, CircleAlert, CircleHelp, Landmark, GitMerge, ArrowRight } from "lucide-react";

const STATUS_META = {
  "Strong": { color: "text-teal-700", bg: "bg-teal-50 border-teal-200", bar: "#0D9488", icon: TrendingUp },
  "Developing": { color: "text-sky-700", bg: "bg-sky-50 border-sky-200", bar: "#0284C7", icon: Wrench },
  "Constrained": { color: "text-amber-700", bg: "bg-amber-50 border-amber-200", bar: "#D97706", icon: CircleAlert },
  "Critical Friction": { color: "text-[#B34A4A]", bg: "bg-red-50/70 border-red-200", bar: "#E06C6C", icon: AlertTriangle },
  "Insufficient Evidence": { color: "text-slate-500", bg: "bg-slate-50 border-slate-200", bar: "#94A3B8", icon: CircleHelp },
};

const BLOCKS = {
  questions: { icon: HelpCircle, title: "HCP questions", tone: "text-amber-700", field: "hcp_questions" },
  barriers: { icon: AlertTriangle, title: "Barriers", tone: "text-[#B34A4A]", field: "barriers" },
  accelerators: { icon: Zap, title: "Accelerators", tone: "text-emerald-700", field: "accelerators" },
  evidence: { icon: FlaskConical, title: "Required evidence", tone: "text-indigo-700", field: "required_evidence" },
  education: { icon: GraduationCap, title: "Educational needs", tone: "text-sky-700", field: "educational_needs" },
  responses: { icon: Target, title: "Potential response options", tone: "text-teal-700", field: "interventions" },
};

const ROLE_EMPHASIS = {
  leadership: ["barriers", "responses", "accelerators", "evidence", "questions", "education"],
  medical: ["questions", "education", "evidence", "barriers", "accelerators", "responses"],
  field: ["questions", "education", "barriers", "accelerators", "evidence", "responses"],
  heor: ["evidence", "barriers", "questions", "responses", "accelerators", "education"],
  access: ["barriers", "responses", "evidence", "questions", "accelerators", "education"],
  default: ["questions", "barriers", "accelerators", "evidence", "education", "responses"],
};

const roleKey = (role) => {
  const id = (role?.id || "").toLowerCase();
  const label = (role?.label || "").toLowerCase();
  if (label.includes("leadership") || id.includes("leader")) return "leadership";
  if (label.includes("access")) return "access";
  if (label.includes("heor") || label.includes("evidence")) return "heor";
  if (label.includes("msl") || label.includes("field")) return "field";
  if (label.includes("medical")) return "medical";
  return "default";
};

const DEPENDENCIES = [
  ["Scientific Understanding", "Patient Identification"],
  ["Patient Identification", "Biomarker Testing"],
  ["Biomarker Testing", "Treatment Consideration"],
  ["Clinical Experience", "Continued Prescribing"],
  ["Continued Prescribing", "Advocacy and Peer Recommendation"],
];

export default function AdoptionJourney({ role, onOpenEvidence }) {
  const [stages, setStages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [active, setActive] = useState(null);
  const [showWhy, setShowWhy] = useState(false);

  useEffect(() => {
    api.get("/adoption").then((r) => {
      setStages(r.data.stages);
      setSummary(r.data.summary);
      const weakest = [...r.data.stages].sort((a, b) => a.health - b.health)[0];
      setActive(weakest?.id);
    });
  }, []);

  const stage = stages.find((s) => s.id === active);
  const order = ROLE_EMPHASIS[roleKey(role)] || ROLE_EMPHASIS.default;

  return (
    <div className="space-y-4" data-testid="adoption-view">
      <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="adoption-journey-strip">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">HCP adoption journey · cross-sectional stage assessment · select a stage</p>
        <p className="text-[11px] text-slate-500 flex items-start gap-1.5 mb-3 max-w-4xl" data-testid="methodology-note">
          <Info className="w-3.5 h-3.5 text-teal-600 shrink-0 mt-0.5" />
          Each stage is assessed independently from the available HCP and scientific evidence. Stage classifications are not conversion rates and do not imply that the same HCPs were tracked across the journey. Arrows indicate conceptual progression only.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {stages.map((s, i) => {
            const meta = STATUS_META[s.status] || STATUS_META["Insufficient Evidence"];
            const SIcon = meta.icon;
            const sel = active === s.id;
            return (
              <button
                key={s.id}
                data-testid={`journey-stage-${s.id}`}
                onClick={() => setActive(s.id)}
                className={`text-left rounded-lg border p-3 transition-colors ${sel ? "border-slate-900 bg-slate-900" : "border-slate-200 bg-white hover:border-teal-400"}`}
              >
                <p className={`font-mono text-[9px] uppercase tracking-wide ${sel ? "text-teal-400" : "text-slate-400"}`}>Stage {i + 1}</p>
                <p className={`text-[11.5px] font-bold leading-tight mt-0.5 min-h-[28px] ${sel ? "text-white" : "text-slate-800"}`}>{s.stage}</p>
                <p className={`text-[10.5px] font-semibold flex items-center gap-1 mt-1 ${sel ? "text-white" : meta.color}`}>
                  <SIcon className="w-3 h-3" style={sel ? {} : { color: meta.bar }} />{s.status}
                </p>
                <p className={`text-[9.5px] mt-0.5 ${sel ? "text-slate-400" : "text-slate-400"}`}>Confidence: {s.assessment_confidence} · {s.insight_ids.length} insights</p>
              </button>
            );
          })}
        </div>
      </div>

      {summary && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="journey-summary">
          <p className="font-mono text-[10px] uppercase tracking-widest text-teal-400 mb-3">Journey-level summary <span className="ml-2 text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5 normal-case font-sans font-medium">Illustrative</span></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
            {[["Strongest stage", summary.strongest_stage], ["Greatest friction", summary.greatest_friction], ["Lowest confidence", summary.lowest_confidence],
              ["Cross-stage barrier", summary.cross_stage_barrier], ["Key evidence gap", summary.evidence_gap], ["Priority response", summary.priority_response]].map(([l, v]) => (
              <div key={l} className="flex items-start gap-2.5">
                <span className="font-mono text-[9px] uppercase tracking-widest text-teal-400 bg-teal-500/10 border border-teal-500/25 rounded px-1.5 py-0.5 mt-0.5 shrink-0 whitespace-nowrap">{l}</span>
                <p className="text-xs text-slate-200 leading-snug">{v}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-3">{summary.friction_reason} · Summary confidence: {summary.confidence} · {summary.note}</p>
        </div>
      )}

      {stage && (
        <div className="bg-white border border-teal-300 ring-1 ring-teal-100 rounded-xl p-6 shadow-sm" data-testid="adoption-stage-detail">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-heading text-base font-bold text-slate-900">
              <span className="font-mono text-slate-400 mr-2">{String(stage.order).padStart(2, "0")}</span>{stage.stage}
            </h3>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className={`text-[10px] ${STATUS_META[stage.status]?.bg} ${STATUS_META[stage.status]?.color}`} data-testid="stage-status-badge">{stage.status}</Badge>
              <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200"><ShieldCheck className="w-3 h-3 mr-1" />Confidence: {stage.assessment_confidence}</Badge>
              <button onClick={() => setShowWhy(!showWhy)} data-testid="why-status-btn"
                className="text-[10px] font-semibold text-teal-700 border border-teal-200 bg-teal-50 rounded-full px-2.5 py-0.5 hover:bg-teal-100">Why this status?</button>
            </div>
          </div>
          {showWhy && (
            <p className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200/80 rounded-lg px-3 py-2 mt-2" data-testid="status-rationale">{stage.status_rationale}</p>
          )}
          <p className="text-sm text-slate-600 leading-relaxed mt-2">{stage.summary}</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-indigo-500" />HCP segments: <span className="font-medium text-slate-700">{stage.segments.join(" · ")}</span></span>
            <span className="flex items-center gap-1.5"><GitMerge className="w-3.5 h-3.5 text-teal-600" />Geographies: <span className="font-medium text-slate-700">{stage.geographies.join(" · ")}</span>
              {stage.market_specific && <span className="text-[9px] font-mono uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 ml-1">Market-specific — do not generalise globally</span>}
            </span>
            {stage.contributing_stakeholders.length > 0 && (
              <span className="flex items-center gap-1.5" data-testid="contributing-stakeholders"><Landmark className="w-3.5 h-3.5 text-slate-400" />Contributing stakeholders: <span className="font-medium text-slate-700">{stage.contributing_stakeholders.join(" · ")}</span></span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
            {order.map((key, idx) => {
              const b = BLOCKS[key];
              return (
                <div key={key} className={`bg-slate-50 border rounded-lg p-3 ${idx < 2 ? "border-teal-300 ring-1 ring-teal-100" : "border-slate-200/80"}`} data-testid={`stage-${key}`}>
                  <p className={`font-mono text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1 ${b.tone}`}>
                    <b.icon className="w-3 h-3" />{b.title}
                    {idx < 2 && role && <span className="ml-auto text-[8px] font-sans font-semibold text-teal-700 bg-teal-100/70 rounded-full px-1.5 py-0.5 normal-case tracking-normal">Priority for {role.label}</span>}
                  </p>
                  <ul className="space-y-1">
                    {(stage[b.field] || []).map((it) => <li key={it} className="text-xs text-slate-700 leading-snug">• {it}</li>)}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-3 border-t border-slate-100">
            <p className="text-[11px] text-slate-500" data-testid="supporting-intelligence">
              Supporting intelligence: <span className="font-semibold text-slate-700">{stage.insight_ids.length} curated HCP statements</span> · illustrative demonstration data
            </p>
            <button
              data-testid="view-evidence-btn"
              onClick={() => onOpenEvidence?.()}
              className="text-xs font-semibold text-teal-700 hover:text-teal-900 flex items-center gap-1"
            >
              View evidence in HCP Voice <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="cross-stage-dependencies">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5"><GitMerge className="w-3.5 h-3.5 text-teal-600" />Cross-stage dependencies · conceptual, not measured</p>
        <div className="flex flex-wrap gap-2">
          {DEPENDENCIES.map(([a, b]) => (
            <span key={a + b} className="flex items-center gap-1.5 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">
              {a} <ArrowRight className="w-3 h-3 text-teal-600" /> {b}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-2.5"><span className="text-[9px] font-mono uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mr-1.5">Cross-stage barrier</span>ESR1 ctDNA testing execution affects Patient Identification, Biomarker Testing and Treatment Consideration — shown once here, not duplicated per stage.</p>
      </div>
    </div>
  );
}
